// Where a connection's ends are, and so its route — the ONE resolver the canvas,
// the hit test, the schedule length, the print and the DXF export all read, so
// none can disagree about where a line runs. DOM-free (unit-tested).
import type {
  Connection,
  ConnectionType,
  ConnEnd,
  ConnOn,
  Discipline,
  Drawing,
  LineStyle,
  PipeType,
  Vec2,
} from '../shared/types';
import {
  DEFAULT_PIPE_TYPES,
  pipeOuter,
} from '../shared/pipeTypes';
import {
  instanceToWorld,
  offsetPolyline,
  pointAtFraction,
} from './geometry';
import {
  externalHeight,
  partSpan,
  polygonBase,
  polygonHeight,
  roomBase,
  wallBase,
  wallHeight,
} from './heights';
import {
  hoseGeometry,
  hosePath,
  type PipeGeom,
  pipeGeometry,
  pipePath,
  type PipePlan,
  pipePlan,
  planOfPath,
  type Vec3,
} from './pipes';
import type { SectionPipe } from './section';
import type { KindObjects } from './drawingObjects';
import type { ItemKind } from './layers';
import {
  heaterWallBands,
  isPolyRoom,
} from './polyRoom';
import { roomDoors } from './door';
import { riserPortHeight } from './risers';
import {
  textBoxCentre,
  textBoxCorners,
} from './textBox';
import {
  anchorAt,
  instanceOutline,
} from './anchors';
import { roomDoor } from './roomDoor';

type Which = 'from' | 'to';

/** A connection's discipline: its own, else mechanical — the ONE default the
 *  plan, the numbering, the filters, 3D, its card and the DXF all read. */
export const connDiscipline = (c: { type?: ConnectionType }): ConnectionType => c.type ?? 'mechanical';

/** What shows the plan's connection lines: the schedule (`showSchedule`, on
 *  while any connection discipline's toggle is) and each discipline's toggle
 *  (absent = shown). */
export interface ConnectionShow {
  schedule: boolean;
  filters?: Partial<Record<Discipline, boolean>>;
}

/** Whether the plan draws a connection's line: only with the schedule on, and
 *  only while its discipline's toggle is. The ONE rule the canvas and the DXF
 *  read. */
export function connectionShown(c: { type?: ConnectionType }, show: ConnectionShow): boolean {
  return show.schedule && show.filters?.[connDiscipline(c)] !== false;
}

const endOf = (c: Connection, which: Which): ConnEnd | undefined => (which === 'from' ? c.fromEnd : c.toEnd);
const idOf = (c: Connection, which: Which): string => (which === 'from' ? c.from : c.to);

const avg = (pts: readonly Vec2[]): Vec2 | null => {
  if (!pts.length) return null;
  let x = 0, y = 0;
  for (const p of pts) { x += p.x; y += p.y; }
  return { x: x / pts.length, y: y / pts.length };
};

/** The names the app gives connection points — its built-ins' and those of the
 *  products it builds the ports of (duct fans, steam conveyors, essence dosers
 *  and the aromarium, Carel generators): its own words, shown in the UI's
 *  language (test/connections.test.mjs lists every port it builds). A name typed
 *  in the product editor is shown as typed, even one that is a word of the
 *  dictionary. */
export const APP_PORT_NAMES: ReadonlySet<string> = new Set([
  'Cold water',
  'Condensate drain',
  'Drain',
  'Drain tempering inlet',
  'Essence outlet',
  'Essence outlet 1',
  'Essence outlet 2',
  'Essence outlet 3',
  'Essence outlet 4',
  'Fan',
  'Hot water',
  'Inlet',
  'Outlet',
  'Steam generator',
  'Steam outlet',
  'Steam outlet 2',
  'Supply',
  'Tank 1',
  'Tank 2',
  'Tank 3',
  'Tank 4',
  'Tank cap 1',
  'Tank cap 2',
  'Tank cap 3',
  'Tank cap 4',
  'Tank drain',
  'Water fill',
]);

/** A connection point's name as shown: the app's own word through `tr`, a typed one as typed. */
export function portName(name: string, tr: (s: string) => string): string {
  return APP_PORT_NAMES.has(name) ? tr(name) : name;
}

/** What a kind of drawing object gives a connection's end glued to it: the list
 *  its objects are kept in, its centre (where an end with no glue sits), the
 *  outline an `along` end runs on (world space, rebuilt every time so a point
 *  glued to a side's midpoint stays on it through a move, a rotation, a resize or
 *  a reshape) and how high an end on it stands by itself. */
export interface ConnHost<T> {
  list: (d: Drawing) => readonly T[] | undefined;
  centre: (o: T) => Vec2 | null;
  outline: (o: T) => { pts: Vec2[]; closed: boolean };
  midHeight: (o: T) => number;
}

/** Every kind of drawing object, and what an end glued to it is given — null: an
 *  end cannot be glued to it. A new kind does not compile until it says. The
 *  hosts are listed in the order a pick tries them (Select's: a text box over a
 *  part over a polygon over a wall or line over a floor). */
const HOSTS = {
  text: {
    list: (d) => d.texts,
    centre: (t) => textBoxCentre(t),
    outline: (t) => ({ pts: textBoxCorners(t), closed: true }),
    midHeight: () => 0, // it stands on the ground
  },
  instance: {
    list: (d) => d.instances,
    centre: (i) => ({ x: i.x, y: i.y }),
    // A node object's run (open), a room's outer wall face, a part's footprint.
    outline: (inst) => instanceOutline(inst),
    midHeight: (i) => { const [a, b] = partSpan(i); return (a + b) / 2; },
  },
  polygon: {
    list: (d) => d.polygons,
    centre: (p) => avg(p.points),
    outline: (p) => ({ pts: p.points, closed: true }),
    midHeight: (p) => polygonBase(p) + polygonHeight(p) / 2, // an extruded shape's; a flat one's base
  },
  wall: {
    list: (d) => d.walls,
    centre: (w) => avg(w.points),
    outline: (w) => ({ pts: w.points, closed: false }), // its centre line
    midHeight: (w) => wallBase(w) + wallHeight(w) / 2,
  },
  flooring: {
    list: (d) => d.floorings,
    centre: (f) => avg(f.points),
    outline: (f) => ({ pts: f.points, closed: true }),
    midHeight: () => 0,
  },
  arc: null,
  cloud: null,
  conn: null,
  dim: null,
  leader: null,
  rcp: null,
  section: null,
  table: null,
} satisfies { [K in ItemKind]: ConnHost<KindObjects[K]> | null };

/** A kind of object a connection's end can be glued to. */
export type ConnHostKind = { [K in ItemKind]: (typeof HOSTS)[K] extends null ? never : K }[ItemKind];
/** The host kinds, in the order a pick tries them. */
export const CONN_HOST_KINDS: readonly ConnHostKind[] = (Object.keys(HOSTS) as ItemKind[]).filter((k): k is ConnHostKind => HOSTS[k] !== null);

type AnyHost = ConnHost<{ id: string }>;
const hostOfKind = (k: ConnHostKind): AnyHost => HOSTS[k] as unknown as AnyHost;

/** The object `id` names, when an end can be glued to it, with its kind. */
export function connHostOf(d: Drawing, id: string): { kind: ConnHostKind; obj: { id: string } } | null {
  if (!id) return null;
  for (const kind of CONN_HOST_KINDS) {
    const obj = hostOfKind(kind).list(d)?.find((o) => o.id === id);
    if (obj) return { kind, obj };
  }
  return null;
}

/** Every object of `d` an end can be glued to, host kind by host kind (`d` may
 *  be a copy's payload: the lists it carries). */
export function connHostObjects(d: Partial<Drawing>): Array<{ kind: ConnHostKind; obj: { id: string } }> {
  return CONN_HOST_KINDS.flatMap((kind) => (hostOfKind(kind).list(d as Drawing) ?? []).map((obj) => ({ kind, obj })));
}

/** The centre of the object `id` names (its host's `centre`): a part's origin, a
 *  polygon's / floor's / wall's vertex average, a text box's centre. Null when it
 *  is gone. */
export function objectCentre(d: Drawing, id: string): Vec2 | null {
  const h = connHostOf(d, id);
  return h ? hostOfKind(h.kind).centre(h.obj) : null;
}

/** The outline an `along` end runs on, in world space (its host's `outline`): a
 *  wall's / line's centre line or a node object's run (open); a polygon, floor,
 *  text box, a room's outer wall face or a part's footprint (closed). */
export function connOutline(d: Drawing, id: string): { pts: Vec2[]; closed: boolean } | null {
  const h = connHostOf(d, id);
  return h ? hostOfKind(h.kind).outline(h.obj) : null;
}

/** Where an `on` end sits on the object `id`. A segment index past a removed
 *  vertex is clamped onto the last segment; a door or heater wall that is gone is
 *  null. */
export function onPoint(d: Drawing, id: string, on: ConnOn): Vec2 | null {
  if (on.kind === 'door' || on.kind === 'heaterWall') {
    const room = d.instances.find((i) => i.id === id);
    if (!room || !isPolyRoom(room)) return null;
    if (on.kind === 'door') {
      const door = roomDoors(room).find((x) => x.id === on.id);
      const j = door ? roomDoor(room, door)?.jambs : null;
      return j ? instanceToWorld(room, { x: (j.start.x + j.end.x) / 2, y: (j.start.y + j.end.y) / 2 }) : null;
    }
    const band = heaterWallBands(room).find((b) => b.wall.id === on.id);
    const mid = band ? avg(band.poly) : null;
    return mid ? instanceToWorld(room, mid) : null;
  }
  if (on.kind === 'side') {
    const text = (d.texts || []).find((t) => t.id === id);
    if (!text) return null;
    const c = textBoxCorners(text);
    const a = c[on.side % 4], b = c[(on.side + 1) % 4];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }
  const o = connOutline(d, id);
  if (!o || o.pts.length < 2) return null;
  const nSegs = o.closed ? o.pts.length : o.pts.length - 1;
  const seg = Math.max(0, Math.min(nSegs - 1, on.seg));
  const a = o.pts[seg], b = o.pts[(seg + 1) % o.pts.length];
  const t = Math.max(0, Math.min(1, on.t));
  const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  if (!on.off) return p;
  const L = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  return { x: p.x - ((b.y - a.y) / L) * on.off, y: p.y + ((b.x - a.x) / L) * on.off };
}

/** Where one end of a connection is: its port, its glue point, its free point,
 *  or — for a connection saved before ends existed — its object's centre. */
export function connEndPoint(d: Drawing, c: Connection, which: Which): Vec2 | null {
  const end = endOf(c, which);
  const id = idOf(c, which);
  if (end?.port) {
    const inst = d.instances.find((i) => i.id === id);
    const port = inst?.ports?.find((p) => p.id === end.port);
    if (inst && port) return instanceToWorld(inst, { x: port.nx * inst.width / 2, y: port.ny * inst.depth / 2 });
  }
  if (end?.on) return onPoint(d, id, end.on);
  if (end?.ref) return anchorAt(d, end.ref);
  if (!id) return end?.p ? { ...end.p } : null;
  return objectCentre(d, id);
}

/** The points a connection is drawn THROUGH: start, its fixed bends, end. Null
 *  when an end's object is gone. */
export function connectionControls(d: Drawing, c: Connection): Vec2[] | null {
  const a = connEndPoint(d, c, 'from');
  const b = connEndPoint(d, c, 'to');
  if (!a || !b) return null;
  return [a, ...(c.waypoints ?? []).map((w) => ({ ...w })), b];
}

const EPS = 1e-6;

/**
 * A right-angle (elbow) path through `ctrl`, PowerPoint's elbow connector: every
 * segment horizontal or vertical. Two points join with a Z — out along the longer
 * run to half way, across, and on. With bends, each leg is an L that leaves
 * perpendicular to how the route arrived (the first along its longer run), so
 * the path never doubles back on itself. `leg[s]` is the leg (control point
 * `leg[s]` → `leg[s] + 1`) segment `s` belongs to.
 */
export function orthogonalPath(ctrl: readonly Vec2[]): { pts: Vec2[]; leg: number[] } {
  const pts: Vec2[] = [{ x: ctrl[0].x, y: ctrl[0].y }];
  const leg: number[] = [];
  const push = (p: Vec2, l: number): void => {
    const last = pts[pts.length - 1];
    if (Math.abs(p.x - last.x) < EPS && Math.abs(p.y - last.y) < EPS) return;
    pts.push({ x: p.x, y: p.y });
    leg.push(l);
  };
  let arrivedH: boolean | null = null;
  for (let i = 0; i < ctrl.length - 1; i++) {
    const p = ctrl[i], q = ctrl[i + 1];
    const dx = q.x - p.x, dy = q.y - p.y;
    const flat = Math.abs(dy) < EPS, plumb = Math.abs(dx) < EPS;
    if (flat || plumb) {
      push(q, i);
      if (!(flat && plumb)) arrivedH = flat;
      continue;
    }
    const alongX = Math.abs(dx) >= Math.abs(dy);
    if (ctrl.length === 2) {
      const mid = alongX ? [{ x: p.x + dx / 2, y: p.y }, { x: p.x + dx / 2, y: q.y }] : [{ x: p.x, y: p.y + dy / 2 }, { x: q.x, y: p.y + dy / 2 }];
      for (const m of mid) push(m, i);
      push(q, i);
      continue;
    }
    const startH: boolean = arrivedH === null ? alongX : !arrivedH;
    push(startH ? { x: q.x, y: p.y } : { x: p.x, y: q.y }, i);
    push(q, i);
    arrivedH = !startH;
  }
  // Two runs in the same direction meeting at a point are one run.
  for (let k = pts.length - 2; k >= 1; k--) {
    const a = pts[k - 1], m = pts[k], b = pts[k + 1];
    const u = { x: m.x - a.x, y: m.y - a.y }, v = { x: b.x - m.x, y: b.y - m.y };
    if (Math.abs(u.x * v.y - u.y * v.x) < EPS && u.x * v.x + u.y * v.y > 0) { pts.splice(k, 1); leg.splice(k, 1); }
  }
  return { pts, leg };
}

/** The type a pipe is made of: the drawing's copy, else the built-in of its id;
 *  null for a connection that is not a pipe or a type nobody knows. */
export function pipeTypeOf(d: Drawing, c: Connection): PipeType | null {
  const id = c.pipe?.type;
  if (!id) return null;
  return (d.pipeTypes ?? []).find((t) => t.id === id) ?? DEFAULT_PIPE_TYPES.find((t) => t.id === id) ?? null;
}

/** A pipe's fall as a fraction (its type's slope %), 0 when it has none. */
export function pipeSlope(d: Drawing, c: Connection): number {
  return Math.max(0, pipeTypeOf(d, c)?.slope ?? 0) / 100;
}

/** The centreline radius a pipe's elbows are drawn at: its type's, else its
 *  outer diameter. */
export function pipeBendRadius(d: Drawing, c: Connection): number {
  const r = pipeTypeOf(d, c)?.bendRadius;
  return r != null && r >= 0 ? r : c.pipe ? pipeOuter(c.pipe) : 0;
}

/** Half way up the object `id` (its host's `midHeight`): a part's or a room's
 *  body, a drawn wall, an extruded shape (a flat one's base); a floor or a text
 *  box stands on the ground. Null when it is gone. */
export function objectMidHeight(d: Drawing, id: string): number | null {
  const h = connHostOf(d, id);
  return h ? hostOfKind(h.kind).midHeight(h.obj) : null;
}

/** The height of the port an end is glued to, when the port has one: its part's
 *  base plus the port's `z`. */
function portHeight(d: Drawing, c: Connection, which: Which): number | null {
  const id = endOf(c, which)?.port;
  if (!id) return null;
  const inst = d.instances.find((i) => i.id === idOf(c, which));
  if (!inst) return null;
  // A riser's port (a shower mixer's hot or cold) is at the riser's top.
  const top = riserPortHeight(d.instances, inst, id);
  if (top != null) return top;
  const z = inst.ports?.find((p) => p.id === id)?.z;
  return z != null ? partSpan(inst)[0] + z : null;
}

/** A pipe's height at one end: its own, else its port's, else half way up its
 *  object, else (a free end) the other end's — so a pipe drawn to a free point
 *  runs level. */
export function pipeEndZ(d: Drawing, c: Connection, which: Which): number {
  const other: Which = which === 'from' ? 'to' : 'from';
  return endOf(c, which)?.z
    ?? portHeight(d, c, which)
    ?? objectMidHeight(d, idOf(c, which))
    ?? endOf(c, other)?.z
    ?? portHeight(d, c, other)
    ?? objectMidHeight(d, idOf(c, other))
    ?? 0;
}

/** A pipe's control points in 3D (its `ConnGeom.ctrl`): its ends at their
 *  heights, each bend at its own, else carried on from the point before at the
 *  pipe's fall. */
function pipeControlsAt(d: Drawing, c: Connection, ctrl: readonly Vec2[], slope: number): Vec3[] {
  const out: Vec3[] = [{ ...ctrl[0], z: pipeEndZ(d, c, 'from') }];
  const wps = c.waypoints ?? [];
  for (let i = 0; i < wps.length; i++) {
    const prev = out[out.length - 1], w = ctrl[i + 1];
    out.push({ x: w.x, y: w.y, z: wps[i].z ?? prev.z - slope * Math.hypot(w.x - prev.x, w.y - prev.y) });
  }
  const last = ctrl[ctrl.length - 1];
  out.push({ x: last.x, y: last.y, z: pipeEndZ(d, c, 'to') });
  return out;
}

/** A pipe's colour off the plan (3D, sections): its discipline's, as on the plan;
 *  a copper pipe's copper. */
const PIPE_COLORS: Record<string, string> = { electrical: '#ff8c1a', plumbing: '#4aa3ff', mechanical: '#9fb0c4', drainage: '#4db891' };
export function pipeColor(d: Drawing, c: Connection): string {
  if (/copper|rame|cuivre/i.test(pipeTypeOf(d, c)?.material ?? '')) return '#b87333';
  return PIPE_COLORS[connDiscipline(c)] ?? PIPE_COLORS.mechanical;
}

/** The pipes as tubes, for the 3D view and the section views: each one `keep`
 *  keeps, along its centreline with its elbows, at its outer and inner radii. */
export function pipeTubes(d: Drawing, keep: (c: Connection) => boolean): SectionPipe[] {
  const out: SectionPipe[] = [];
  for (const c of d.connections || []) {
    if (!c.pipe || !keep(c)) continue;
    const g = connGeom(d, c);
    if (!g?.pipe) continue;
    out.push({ id: c.id, pts: g.pipe.geom.centre, r: g.pipe.outer / 2, ri: c.pipe.inner / 2, color: g.color });
  }
  return out;
}

/** A schematic connection's run in 3D (mm): 50 under the top of the drawn wall
 *  nearest its route, 2650 with none, and at least 100 over every roof it passes
 *  over. */
export const CONN_RUN_BELOW_WALL = 50;
export const DEFAULT_WALL_TOP = 2650;
export const CONN_OVER_ROOF = 100;
/** The centre spacing (mm) of a schematic connection's parallel lines in 3D. */
export const CONN_LINE_PITCH_3D = 30;
/** A connection's dash in 3D (mm along the line): the plan's pattern
 *  (`connectionDash`, screen px) ten times over, as the plan shows it at 1:100. */
export function connDash3d(style: LineStyle | undefined): number[] {
  switch (style ?? 'dashed') {
    case 'solid': return [];
    case 'dotted': return [15, 50];
    case 'dash-dot': return [90, 50, 15, 50];
    case 'dashed':
    default: return [90, 60];
  }
}

const segPointDist = (p: Vec2, a: Vec2, b: Vec2): number => {
  const dx = b.x - a.x, dy = b.y - a.y, L2 = dx * dx + dy * dy;
  const t = L2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / L2)) : 0;
  return Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy);
};
const cross = (o: Vec2, a: Vec2, b: Vec2): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
const segsCross = (a: Vec2, b: Vec2, c: Vec2, e: Vec2): boolean => {
  const d1 = cross(a, b, c), d2 = cross(a, b, e), d3 = cross(c, e, a), d4 = cross(c, e, b);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
};
/** The plan distance between two segments (0 where they cross). */
const segSegDist = (a: Vec2, b: Vec2, c: Vec2, e: Vec2): number =>
  (segsCross(a, b, c, e) ? 0 : Math.min(segPointDist(a, c, e), segPointDist(b, c, e), segPointDist(c, a, b), segPointDist(e, a, b)));
const inRing = (p: Vec2, ring: readonly Vec2[]): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
};

/** The top (mm above the ground) of the wall drawn with the Wall tool nearest a
 *  plan path — a room's own walls are not walls here, nor is a line; null with
 *  none drawn. */
export function nearestWallTop(d: Drawing, path: readonly Vec2[]): number | null {
  let best = Infinity, top: number | null = null;
  for (const w of d.walls || []) {
    if (w.style === 'line') continue;
    for (let k = 0; k + 1 < w.points.length; k++) {
      const a = w.points[k], b = w.points[k + 1];
      const dist = path.length === 1 ? segPointDist(path[0], a, b) : Math.min(...path.slice(1).map((q, i) => segSegDist(path[i], q, a, b)));
      if (dist < best) { best = dist; top = wallBase(w) + wallHeight(w); }
    }
  }
  return top;
}

/** The tallest roof (a room's Height over its base, mm above the ground) of the
 *  rooms a plan path runs inside or crosses; null when it passes over none. */
export function tallestRoofUnder(d: Drawing, path: readonly Vec2[]): number | null {
  let top: number | null = null;
  for (const inst of d.instances) {
    if (!isPolyRoom(inst)) continue;
    const ring = inst.polygon!.map((p) => instanceToWorld(inst, p));
    const over = path.some((p) => inRing(p, ring))
      || path.slice(1).some((q, i) => ring.some((a, k) => segsCross(path[i], q, a, ring[(k + 1) % ring.length])));
    if (over) top = Math.max(top ?? -Infinity, roomBase(inst) + externalHeight(inst));
  }
  return top;
}

/** The height a schematic connection runs at in 3D by itself: 50 under the top
 *  of the drawn wall nearest its route (2650 with none), raised to 100 over the
 *  tallest roof it passes over. */
export function connRunAuto(d: Drawing, route: readonly Vec2[]): number {
  const wall = (nearestWallTop(d, route) ?? DEFAULT_WALL_TOP) - CONN_RUN_BELOW_WALL;
  const roof = tallestRoofUnder(d, route);
  return roof == null ? wall : Math.max(wall, roof + CONN_OVER_ROOF);
}

/** A schematic connection's line in 3D: one per parallel line, 1 px wide. */
export interface ConnLine3d {
  id: string;
  color: string;
  /** The line's pieces, each a start and an end: the whole path for a solid
   *  line, its dashes for any other. */
  segs: [Vec3, Vec3][];
}

/** The schematic connections as lines for the 3D view: each one `keep` keeps,
 *  a line per parallel line (`lines`), side by side `CONN_LINE_PITCH_3D` apart
 *  and centred on the route — up the risers too —, in its discipline's colour
 *  and dashed as its `style` (`connDash3d`). */
export function connLines3d(d: Drawing, keep: (c: Connection) => boolean): ConnLine3d[] {
  const out: ConnLine3d[] = [];
  for (const c of d.connections || []) {
    if (c.pipe || !keep(c)) continue;
    const r = connGeom(d, c);
    if (!r?.run) continue;
    const g = r.path;
    const n = Math.max(1, Math.round(c.lines || 1));
    const dash = connDash3d(c.style);
    // The plan route with its repeated points (a riser's foot and top) merged;
    // `of[i]` is the plan point the 3D point `i` stands on.
    const plan: Vec2[] = [], of: number[] = [];
    for (const p of g.pts) {
      const last = plan[plan.length - 1];
      if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1e-6) plan.push({ x: p.x, y: p.y });
      of.push(plan.length - 1);
    }
    for (let k = 0; k < n; k++) {
      const off = (k - (n - 1) / 2) * CONN_LINE_PITCH_3D;
      const moved = off === 0 || plan.length < 2 ? plan : offsetPolyline(plan, off, false);
      out.push({ id: c.id, color: r.color, segs: dashPath3(g.pts.map((p, i) => ({ ...moved[of[i]], z: p.z })), dash) });
    }
  }
  return out;
}

/** A 3D polyline cut into the dashes of `pattern` (mm: on, off, on, off, …; the
 *  pattern runs on round its corners, as a canvas dash does); with no pattern,
 *  its segments. Zero-length segments are dropped. */
export function dashPath3(pts: readonly Vec3[], pattern: readonly number[]): [Vec3, Vec3][] {
  const out: [Vec3, Vec3][] = [];
  const at = (a: Vec3, b: Vec3, t: number): Vec3 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t });
  const total = pattern.reduce((s, v) => s + v, 0);
  if (!pattern.length || total <= 0) {
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1], b = pts[i];
      if (Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z) > 1e-9) out.push([a, b]);
    }
    return out;
  }
  let k = 0, left = pattern[0];
  let open: Vec3 | null = null;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
    if (len <= 1e-9) continue;
    let s = 0;
    if (k % 2 === 0 && !open) open = a;
    while (len - s > left + 1e-9) {
      s += left;
      const p = at(a, b, s / len);
      if (k % 2 === 0) { if (open) out.push([open, p]); open = null; } else open = p;
      k = (k + 1) % pattern.length;
      left = pattern[k];
    }
    left -= len - s;
    if (k % 2 === 0 && open) { out.push([open, b]); open = b; }
  }
  return out.filter(([a, b]) => Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z) > 1e-9);
}

/** The connection's drawn route and, per segment, the leg it belongs to — its
 *  control points joined straight, or with right angles (`route: 'orthogonal'`);
 *  a pipe's route seen from above. */
export function connectionRouteLegs(d: Drawing, c: Connection): { pts: Vec2[]; leg: number[] } | null {
  return connGeom(d, c)?.plan ?? null;
}

/** The connection's route as drawn. Null when an end's object is gone. */
export function connectionRoute(d: Drawing, c: Connection): Vec2[] | null {
  return connGeom(d, c)?.plan.pts ?? null;
}

/** A pipe as `connGeom` resolves it. */
export interface ConnPipeGeom {
  /** Its type: the drawing's copy, else the built-in; null for one nobody knows. */
  readonly type: PipeType | null;
  /** Its fall as a fraction, 0 with none. */
  readonly slope: number;
  /** A flexible hose: one smooth curve, its bends never snapped. */
  readonly flexible: boolean;
  /** Its outer diameter (mm). */
  readonly outer: number;
  /** Its centreline with its elbows (a hose's curve as it runs): what the 3D
   *  view, the section views, the Pipe table and its length read. */
  readonly geom: PipeGeom;
  /** It from above, to scale: its sides, centreline and risers — the plan's
   *  drawing of it and the DXF's. */
  readonly plan: PipePlan;
}

/** A connection resolved once (docs/claude/connections.md *One resolved
 *  connection*): what the plan, the hit test, its tag, 3D, the section views,
 *  the reflected ceiling plans, the DXF, the tables and its handles read of it.
 *  Whether it is a pipe or a schematic line is decided here and nowhere else:
 *  each reads the same fields of either. Each field is worked out the first time
 *  it is read (a line's run height scans the walls and roofs). */
export interface ConnGeom {
  /** Its discipline (absent: mechanical). */
  readonly discipline: ConnectionType;
  /** Its colour off the plan (3D, sections, ceiling plans). */
  readonly color: string;
  /** Its control points in 3D: its ends at their heights, then its bends — a
   *  pipe's at their own or on its fall, a line's at its run height. */
  readonly ctrl: Vec3[];
  /** Its route seen from above, with the leg (control point to the next) each
   *  segment belongs to. */
  readonly plan: { pts: Vec2[]; leg: number[] };
  /** Its route in 3D with the leg of each segment: a pipe's corners (45° / 90°,
   *  its fall, its risers; a hose's curve), a line's up from each end to its run
   *  height (or down), along its plan route there and down to the other end. */
  readonly path: { pts: Vec3[]; leg: number[] };
  /** How far it runs in 3D (mm): a pipe along its elbows, a line along `path`. */
  readonly runLength: number;
  /** Where its tag stands on the plan route: `labelT` of the way along, else
   *  half way. */
  readonly tag: Vec2;
  /** A line's run height and the one it takes by itself; null for a pipe. */
  readonly run: { z: number; auto: number } | null;
  /** The pipe it is made of; null for a schematic line. */
  readonly pipe: ConnPipeGeom | null;
}

/** A connection resolved once (`ConnGeom`); null when an end's object is gone. */
export function connGeom(d: Drawing, c: Connection): ConnGeom | null {
  const at = connectionControls(d, c);
  if (!at) return null;
  return c.pipe ? pipeConnGeom(d, c, c.pipe, at) : lineConnGeom(d, c, at);
}

const length3d = (pts: readonly Vec3[]): number => {
  let L = 0;
  for (let i = 1; i < pts.length; i++) L += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y, pts[i].z - pts[i - 1].z);
  return L;
};
const tagOn = (c: Connection, plan: Vec2[]): Vec2 => pointAtFraction(plan, c.labelT ?? 0.5);

/** A pipe resolved: its route in 3D (`pipePath`: 45° / 90°, its fall, risers;
 *  a flexible hose's `hosePath`, one smooth curve), its elbows on that route
 *  (`pipeGeometry` / `hoseGeometry`) and its plan from above (`planOfPath`, the
 *  plan route; `pipePlan`, its outline). */
function pipeConnGeom(d: Drawing, c: Connection, size: { inner: number; wall: number }, at: readonly Vec2[]): ConnGeom {
  const type = pipeTypeOf(d, c), slope = pipeSlope(d, c), flexible = !!type?.flexible, outer = pipeOuter(size);
  const ctrl = pipeControlsAt(d, c, at, slope);
  const path = flexible ? hosePath(ctrl) : pipePath(ctrl, slope);
  let plan: { pts: Vec2[]; leg: number[] } | undefined, geom: PipeGeom | undefined, view: PipePlan | undefined, color: string | undefined;
  const geomOf = (): PipeGeom => (geom ??= flexible ? hoseGeometry(path.pts) : pipeGeometry(path.pts, pipeBendRadius(d, c)));
  const planOf = (): { pts: Vec2[]; leg: number[] } => (plan ??= planOfPath(path));
  const pipe: ConnPipeGeom = {
    type, slope, flexible, outer,
    get geom() { return geomOf(); },
    get plan() { return (view ??= pipePlan(geomOf(), outer)); },
  };
  return {
    discipline: connDiscipline(c),
    get color() { return (color ??= pipeColor(d, c)); },
    ctrl, path,
    get plan() { return planOf(); },
    get runLength() { return geomOf().length; },
    get tag() { return tagOn(c, planOf().pts); },
    run: null, pipe,
  };
}

/** A schematic line resolved (docs/claude/connections.md *Connections in 3D*):
 *  its control points joined straight, or with right angles (`route:
 *  'orthogonal'`); in 3D, from each end at its height (`pipeEndZ`) straight up —
 *  or down — to its run height (its own `runZ`, else `connRunAuto`), along its
 *  plan route there and down to the other end, each riser on the leg it rises
 *  from. */
function lineConnGeom(d: Drawing, c: Connection, at: readonly Vec2[]): ConnGeom {
  const plan = c.route === 'orthogonal' ? orthogonalPath(at) : { pts: at.map((p) => ({ x: p.x, y: p.y })), leg: at.slice(1).map((_, i) => i) };
  let run: { z: number; auto: number } | undefined, path: { pts: Vec3[]; leg: number[] } | undefined, ctrl: Vec3[] | undefined, color: string | undefined;
  const runOf = (): { z: number; auto: number } => {
    if (run) return run;
    const auto = connRunAuto(d, plan.pts);
    return (run = { z: c.runZ ?? auto, auto });
  };
  const pathOf = (): { pts: Vec3[]; leg: number[] } => {
    if (path) return path;
    const z = runOf().z, a = plan.pts[0], b = plan.pts[plan.pts.length - 1];
    const pts: Vec3[] = [{ x: a.x, y: a.y, z: pipeEndZ(d, c, 'from') }, ...plan.pts.map((p) => ({ x: p.x, y: p.y, z })), { x: b.x, y: b.y, z: pipeEndZ(d, c, 'to') }];
    const leg = [plan.leg[0] ?? 0, ...plan.leg, plan.leg[plan.leg.length - 1] ?? 0];
    return (path = { pts, leg });
  };
  return {
    discipline: connDiscipline(c),
    get color() { return (color ??= pipeColor(d, c)); },
    get ctrl() {
      if (ctrl) return ctrl;
      const z = runOf().z, last = at.length - 1;
      return (ctrl = at.map((p, i) => ({ x: p.x, y: p.y, z: i === 0 ? pipeEndZ(d, c, 'from') : i === last ? pipeEndZ(d, c, 'to') : z })));
    },
    plan,
    get path() { return pathOf(); },
    get runLength() { return length3d(pathOf().pts); },
    get tag() { return tagOn(c, plan.pts); },
    get run() { return runOf(); },
    pipe: null,
  };
}

/** Whether a connection has an end on any of `ids`. `from` / `to` always name the
 *  object an end is glued to (`anchorObjectId` of its anchor), so they decide. */
export function connectionTouches(c: Connection, ids: ReadonlySet<string>): boolean {
  return (!!c.from && ids.has(c.from)) || (!!c.to && ids.has(c.to));
}

/** Whether an end is glued to a door or heater wall of room `roomId` that no
 *  longer exists — the connection has lost that end. */
export function endOnGoneFeature(d: Drawing, c: Connection, roomId: string): boolean {
  for (const which of ['from', 'to'] as const) {
    const on = endOf(c, which)?.on;
    if (idOf(c, which) === roomId && on && (on.kind === 'door' || on.kind === 'heaterWall') && !onPoint(d, roomId, on)) return true;
  }
  return false;
}

/** One end of `c` glued to the object `id` at `end` (a pick's: `''` and a `p`
 *  for a free point), keeping a height typed for that end — where it is glued
 *  changes, how high it stands does not. Mutates `c`. */
export function reglueEnd(c: Connection, which: Which, id: string, end: ConnEnd | undefined): void {
  const z = endOf(c, which)?.z;
  const next: ConnEnd | undefined = end ? structuredClone(end) : z != null ? {} : undefined;
  if (next && z != null) next.z = z;
  if (which === 'from') { c.from = id; if (next) c.fromEnd = next; else delete c.fromEnd; } else { c.to = id; if (next) c.toEnd = next; else delete c.toEnd; }
}

/** The same connection run the other way: ends swapped (glue and all) and the
 *  bends in reverse order. Mutates `c`. */
export function reverseConnection(c: Connection): void {
  [c.from, c.to] = [c.to, c.from];
  const f = c.fromEnd, t = c.toEnd;
  if (t) c.fromEnd = t; else delete c.fromEnd;
  if (f) c.toEnd = f; else delete c.toEnd;
  if (c.waypoints) c.waypoints.reverse();
  // A pipe's bends keep their heights; its fall now runs the other way.
  if (c.labelT != null) c.labelT = 1 - c.labelT; // the tag stays where it is on the line
}
